// Toast — small, transient, bottom-of-screen status message.
//
// Used for cases where an action silently fails or completes off-screen
// (e.g. kid taps "Start math practice" but the session builder returns
// null because the item pool was empty). Without a toast the kid taps
// and nothing visibly happens — the worst kind of UX bug.
//
// API: pass a string `message` to show, null to hide. Caller controls
// dismissal timing via setTimeout — the component itself is dumb. Two
// kinds: "error" (red accent for things that didn't work) and "info"
// (plum accent for neutral confirmations).

import { useEffect, useState } from "react";
import { COLORS, RADIUS, SHADOW, SPACE, TYPE } from "../../styles/theme.js";

export default function Toast({ message, kind = "info", onDismiss }) {
  // Mount/unmount with a short slide so the toast doesn't pop into existence.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (message) setVisible(true);
    else setVisible(false);
  }, [message]);

  if (!message) return null;

  const accent = kind === "error" ? COLORS.raspberry : COLORS.plum;
  return (
    <div
      data-testid="cloud-toast"
      role={kind === "error" ? "alert" : "status"}
      aria-live="polite"
      // H-A4 — passive status toast must NOT steal taps: it floats over the
      // bottom-center band where primary CTAs live (onboarding Continue,
      // session buttons). pointer-events: none on the container (the Dismiss
      // button re-enables itself) so a 6s info toast never blocks a tap.
      style={{
        position: "fixed",
        pointerEvents: "none",
        left: "50%",
        bottom: visible ? SPACE[6] : -80,
        transform: "translateX(-50%)",
        background: COLORS.surface,
        color: COLORS.text,
        borderLeft: `4px solid ${accent}`,
        boxShadow: SHADOW.card,
        borderRadius: RADIUS.lg,
        padding: `${SPACE[3]}px ${SPACE[5]}px`,
        fontSize: TYPE.body.size,
        fontWeight: 700,
        fontFamily: "inherit",
        maxWidth: "min(420px, calc(100vw - 32px))",
        zIndex: 1000,
        transition: "bottom 0.28s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease",
        opacity: visible ? 1 : 0,
        display: "flex", alignItems: "center", gap: SPACE[3],
      }}
    >
      <span style={{ fontSize: 20, lineHeight: 1 }}>{kind === "error" ? "⚠️" : "ℹ️"}</span>
      <span style={{ flex: 1, lineHeight: 1.4 }}>{message}</span>
      {onDismiss && (
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            background: "transparent",
            pointerEvents: "auto",
            border: "none",
            fontSize: 18,
            cursor: "pointer",
            color: COLORS.muted,
            padding: SPACE[1],
            fontFamily: "inherit",
            // MA-285 — 44px touch floor: the toast overlays every kid route,
            // so its Dismiss "×" must be reliably tappable by a 4-7yo finger,
            // not just legible.
            minWidth: 44,
            minHeight: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >×</button>
      )}
    </div>
  );
}
